import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { VscInfo } from "react-icons/vsc";

import useActionSupplier from "../../../../../../hooks/useActionSupplier";
import { Supplier, SupplierIds } from "../../../../../../models/supplier";
import { useAppState } from "../../../../../../context/StateContext";
import ConfirmationModal from "../../../../../../components/Dialogs/ConfirmationModal";
import InviteSupplierModalSkeleton from "./skeleton";
import useStyles from "./styles";

export interface InviteSupplierModalHandles {
  open: (suppliers: SupplierIds) => void;
  close: () => void;
}
interface InviteSupplierModalProps {
  onSuccess?: () => void;
}

const InviteSupplierModal: ForwardRefRenderFunction<
  InviteSupplierModalHandles,
  InviteSupplierModalProps
> = (props, ref) => {
  const { onSuccess } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { getSuppliersById, useInviteSuppliers } = useActionSupplier();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [supplierIds, setSupplierIds] = useState<SupplierIds>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [requestsValid, setRequestsValid] = useState<number>(0);

  const haveManySuppliers = suppliers.length > 1;

  const openModal = useCallback((receivedSupplierIds: SupplierIds) => {
    setSupplierIds(receivedSupplierIds);
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setSupplierIds([]);
    setSuppliers([]);
    setModalOpened(false);
    setRequestsValid(0);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const { isFetching: loadingSuppliersData } = getSuppliersById(supplierIds, setSuppliers);
  const inviteActions = useInviteSuppliers(supplierIds, Number(currentUser?.companyId), () =>
    setRequestsValid((prev) => prev + 1)
  );

  const allLoadings = loadingSuppliersData || inviteActions.some((invite) => invite.isFetching);

  useEffect(() => {
    if (requestsValid > 0 && requestsValid === suppliers.length) {
      closeModal();
      if (onSuccess) onSuccess();
    }
  }, [requestsValid, suppliers]);

  const onSubmitInvite = () => {
    inviteActions.map((invite) => invite.refetch());
  };

  const renderContentBySuppliersToRequest = () => {
    if (loadingSuppliersData) {
      return <InviteSupplierModalSkeleton />;
    }
    if (!suppliers.length) return null;

    if (haveManySuppliers) {
      return (
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.supplierName}>
              <span data-cy="invite-supplier-name">
                {`${translate("doYouWantToInvite")} ${suppliers.length} ${translate(
                  "suppliers"
                )} ${translate("toShipeezi")}`}
              </span>
            </div>
          </Grid>
          <Grid item>
            <div className={classes.supplierEmail}>
              <span data-cy="invite-supplier-email">{`${translate(
                "anInvitationWillBeSendTo"
              )} ${translate("suppliers")} ${translate("emails")}`}</span>
            </div>
          </Grid>
        </Grid>
      );
    }

    const supplier = suppliers[0];

    return (
      <Grid container spacing={2}>
        <Grid item>
          <div className={classes.supplierName}>
            {`${translate("doYouWantToInvite")} `}
            <span data-cy="invite-supplier-name">{`${supplier?.name}`}</span>
            {` ${translate("toShipeezi")}`}
          </div>
        </Grid>
        <Grid item>
          <div className={classes.supplierEmail}>
            {`${translate("anInvitationWillBeSendTo")} `}
            <span data-cy="invite-supplier-email">{`${supplier?.email}`}</span>
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <ConfirmationModal
      width="520px !important"
      open={modalOpened}
      closeModal={closeModal}
      type="success"
      title={translate("inviteSupplier")}
      leftIconTitle={<VscInfo color="#12B76A" size={20} />}
      loading={allLoadings}
      textSubmit={translate("inviteSupplier")}
      submit={onSubmitInvite}
    >
      <div className={classes.container} data-cy="invite-supplier-container">
        {renderContentBySuppliersToRequest()}
      </div>
    </ConfirmationModal>
  );
};

export default forwardRef(InviteSupplierModal);
